(function () {
    'use strict';

    angular
        .module('app')
        .directive('drvEntityFieldSelector', drvEntityFieldSelector);

    drvEntityFieldSelector.$inject = [];

    /**
     * @ngdoc directive
     * @name drvEntityFieldSelector
     *
     * @description Tree control for entity field selection.
     *
     * This is using the "WiX Angular Tree Control"
     * http://wix.github.io/angular-tree-control/
     *
     * # Scope Bindings
     * {object} entityTemplate - The entity template to show fields for.
     * {array} entityTemplates - Related entity templates, and if 'includeOrganisation' and/or 'includeCurrentUser' is true, 
     *   the organisation/current user entity template and any of its related entity templates.
     * {bool} includeOrganisation - Optional, default is false. True if fields for the 'Organisation'
     *   entity template are to be included.
     * {bool} includeCurrentUser - Optional, default is false. True if fields for the 'Current User'
     *   are to be included.
     * @param {bool} includeDocumentTemplate - Optional, default is false. True if fields for the 'Document Template'
     *   are to be included in the Insert Field dialog.
     * @param {bool} includeFileDetails - Optional, default is false. True if fields for the 'File'
     *   are to be included in the Insert Field dialog.
     * @param {bool} excludeFileDetailsName - Optional, default is false. True if 'Name' field for the 'File'
     *   is to be excluded in the Insert Field dialog.
     * @param {bool} includeEntitySystem - Optional, default is false. True if system fields for the entity
     *   are to be included in the Insert Field dialog.
     * @param {bool} includeEntitySystemForPreview - Optional, default is false. True if system fields for the entity preview
     *   are to be included in the Insert Field dialog.
     * @param {bool} includeEntitySystemForSearch - Optional, default is false. True if system fields for the entity search
     *   are to be included in the Insert Field dialog.
     * {bool} firstLevelFieldsOnly - Optional, default is false. True if only the first-level fields
     *   of the entity template should be shown.
     * {bool} multiSelect - Optional, default is false. True if it should be possible for multiple
     *   fields to be selected.
     * {object} fieldFormatter - Optional, default is FieldSelectorService.fieldFormatters.simple
     *   One of the field formatters in FieldSelectorService.fieldFormatters
     * {array} selectedFields - Optional. Array of {string} for selected fields, in the format of the
     *   provided field formatter, e.g. ['[[ChilliDocs:Cats.Species]]', '[[ChilliDocs:Cats.Name]]']
     *
     */
    function drvEntityFieldSelector() {
        var directive = {
            restrict: 'E',
            templateUrl: 'app/FieldSelector/entity-field-selector.tmpl.html',
            scope: {
                entityTemplate: '<',
                entityTemplates: '<',
                includeOrganisation: '<?',
                includeCurrentUser: '<?',
                includeDocumentTemplate: '<?',
                includeFileDetails: '<?',
                excludeFileDetailsName: '<?',
                includeEntitySystem: '<?',
                includeEntitySystemForPreview: '<?',
                includeEntitySystemForSearch: '<?',
                firstLevelFieldsOnly: '<?',
                multiSelect: '<?',
                fieldFormatter: '<?',
                selectedFields: '=?'
            },
            bindToController: true,
            controller: drvFieldSelectorCtrl,
            controllerAs: 'vm'
        };
        return directive;
    }

    drvFieldSelectorCtrl.$inject = ['$scope', 'FieldSelectorService', 'FieldTypesService'];

    function drvFieldSelectorCtrl($scope, FieldSelectorService, FieldTypesService) {
        /* jshint validthis:true */
        var vm = this;
        vm.formatTooltip = formatTooltip;
        vm.treeData = [];
        vm.selectedTreeNode = null;
        vm.selectedTreeNodes = [];
        vm.expandedTreeNodes = [];
        vm.fieldTypes = [];
        vm.treeOptions = {
            templateUrl: 'angular-tree-control.tmpl.html',
            multiSelection: vm.multiSelect,
            isSelectable: isTreeNodeSelectable
        };
        vm.expandAll = expandAll;
        vm.collapseAll = collapseAll;

        activate();

        function activate() {
            if (_.isNil(vm.multiSelect)) {
                vm.multiSelect = false;
            }

            if (_.isNil(vm.fieldFormatter)) {
                vm.fieldFormatter = FieldSelectorService.fieldFormatters.simple;
            }

            if (_.isNil(vm.selectedFields)) {
                vm.selectedFields = [];
            }

            vm.treeOptions.multiSelection = vm.multiSelect;

            loadEntityFieldTypes();

            // When the entity template is changed, need to update the tree nodes.
            $scope.$watch('vm.entityTemplate', updateTreeData);

            $scope.$watch('vm.selectedTreeNode', function () {
                _.remove(vm.selectedFields);
                if (!_.isNil(vm.selectedTreeNode) && !_.isNil(vm.selectedTreeNode.fieldExpression)) {
                    vm.selectedFields.push(vm.selectedTreeNode.fieldExpression);
                }
            });

            $scope.$watch('vm.selectedFields', function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    updateSelectedNodes();
                }
            }, true);

            $scope.$watch('vm.selectedTreeNodes', function () {
                updateSelectedFields();
            }, true);
        }


        /**
         * Update the selected nodes based on the selected fields list (vm.selectedFields).
         */
        function updateSelectedNodes() {
            var nodesToBeSelected = findNodesByFieldExpression(vm.selectedFields, vm.treeData);

            if (vm.multiSelect) {

                // Remove all nodes that shouldn't be selected
                _.remove(vm.selectedTreeNodes, function (node) {
                    return nodesToBeSelected.indexOf(node) === -1;
                });

                // Add all nodes that should be selected
                angular.forEach(nodesToBeSelected, function (node) {
                    if (vm.selectedTreeNodes.indexOf(node) === -1) {
                        vm.selectedTreeNodes.push(node);
                    }
                });

            } else {
                if (nodesToBeSelected.length === 0) {
                    vm.selectedTreeNode = null;
                } else if (vm.selectedTreeNode !== nodesToBeSelected[0]) {
                    vm.selectedTreeNode = nodesToBeSelected[0];
                }
            }
        }

        /**
         * Update the list of selected fields (vm.selectedFields) based on the nodes selected in the tree.
         */
        function updateSelectedFields() {
            _.remove(vm.selectedFields);

            if (vm.multiSelect) {
                if (!_.isNil(vm.selectedTreeNodes)) {
                    angular.forEach(vm.selectedTreeNodes, function (node) {
                        if (!_.isNil(node.fieldExpression)) {
                            vm.selectedFields.push(node.fieldExpression);
                        }
                    });
                }
            } else {
                if (!_.isNil(vm.selectedTreeNode)) {
                    vm.selectedFields.push(vm.selectedTreeNode.fieldExpression);
                }
            }
        }

        /**
         * Update the tree data based on the entity template.
         */
        function updateTreeData(entityTemplate) {
            vm.treeData = [];

            var firstLevelFieldsOnly = false;
            if (!_.isNil(vm.firstLevelFieldsOnly) && vm.firstLevelFieldsOnly) {
                firstLevelFieldsOnly = vm.firstLevelFieldsOnly;
            }

            if (!_.isNil(entityTemplate)) {
                var entityTemplateNodes = FieldSelectorService.convertEntityTemplateFields({
                    fields: entityTemplate.Fields,
                    templates: vm.entityTemplates,
                    firstLevelFieldsOnly: firstLevelFieldsOnly,
                    fieldFormatter: vm.fieldFormatter
                });

                vm.treeData.push({
                    label: entityTemplate.Name,
                    children: entityTemplateNodes,
                    isSelectable: false
                });

                if (!_.isNil(vm.includeOrganisation) && vm.includeOrganisation) {
                    var organisationTemplate = _.find(vm.entityTemplates, { EntityType: 'Organisation' });

                    if (_.isNil(organisationTemplate)) {
                        console.error('Could not find entity template for "Organisation"');
                    } else if (organisationTemplate.Id !== vm.entityTemplate.Id) {
                        var organisationNodes = FieldSelectorService.convertEntityTemplateFields({
                            fields: organisationTemplate.Fields,
                            templates: vm.entityTemplates,
                            firstLevelFieldsOnly: firstLevelFieldsOnly,
                            fieldFormatter: vm.fieldFormatter,
                            fieldExpressionPrefix: '$.Organisation.'
                        });

                        vm.treeData.push({
                            label: organisationTemplate.Name,
                            children: organisationNodes,
                            isSelectable: false
                        });
                    }
                }

                if (!_.isNil(vm.includeCurrentUser) && vm.includeCurrentUser) {
                    var currentUserTemplate = _.find(vm.entityTemplates, { EntityType: 'User' });

                    if (_.isNil(currentUserTemplate)) {
                        console.error('Could not find entity template for "User"');
                    } else {
                        var currentUserNodes = FieldSelectorService.convertEntityTemplateFields({
                            fields: currentUserTemplate.Fields,
                            templates: vm.entityTemplates,
                            firstLevelFieldsOnly: firstLevelFieldsOnly,
                            fieldFormatter: vm.fieldFormatter,
                            fieldExpressionPrefix: '$.Current User.'
                        });

                        vm.treeData.push({
                            label: 'Current User',
                            children: currentUserNodes,
                            isSelectable: false
                        });
                    }
                }

                if (!_.isNil(vm.includeDocumentTemplate) && vm.includeDocumentTemplate) {
                    vm.treeData.push({
                        label: 'Document Template',
                        children: FieldSelectorService.getDocumentTemplateFields(vm.fieldFormatter),
                        isSelectable: false
                    });
                }

                if (!_.isNil(vm.includeFileDetails) && vm.includeFileDetails) {
                    vm.treeData.push({
                        label: 'File',
                        children: FieldSelectorService.getFileFields(vm.fieldFormatter, vm.excludeFileDetailsName),
                        isSelectable: false
                    });
                }

                if (!_.isNil(entityTemplate) && vm.includeEntitySystem) {
                    vm.treeData.push({
                        label: 'System:' + entityTemplate.Name,
                        children: FieldSelectorService.getEntitySystemFields(vm.fieldFormatter, entityTemplate.Name),
                        isSelectable: false
                    });
                }

                if (!_.isNil(entityTemplate) && vm.includeEntitySystemForPreview) {
                    vm.treeData.push({
                        label: 'System:' + entityTemplate.Name,
                        children: FieldSelectorService.getEntitySystemForPreviewFields(vm.fieldFormatter, entityTemplate.Name),
                        isSelectable: false
                    });
                }

                if (!_.isNil(entityTemplate) && vm.includeEntitySystemForSearch) {
                    vm.treeData.push({
                        label: 'System:' + entityTemplate.Name,
                        children: FieldSelectorService.getEntitySystemForSearchFields(vm.fieldFormatter, entityTemplate.Name),
                        isSelectable: false
                    });
                }

                updateSelectedNodes();
                expandNodesWithSelectedChildren();
            }
        }

        /**
         * Load entity field types asynchronously.
         * A field type is, e.g. "Text".
         * Need this data here because it includes the "Attributes", which define extra properties for fields,
         * like "Length", etc. These properties are presented in the tooltips.
         */
        function loadEntityFieldTypes() {
            FieldTypesService.getFieldTypes()
                .then(getFieldTypesOk);

            function getFieldTypesOk(data) {
                vm.fieldTypes = data;
            }
        }

        /**
         * Formats a HTML tooltip for the provided node.
         *
         * @param {object} node
         * @returns {string} HTML formatted tooltip
         */
        function formatTooltip(node) {
            if (!node.field) {
                return '';
            }

            var tip = '<h3>' + node.field.Name + '</h3>';
            if (node.field.Description !== '') {
                tip += node.field.Description + '<br />';
            }
            tip += '<strong>Mandatory: </strong>' + (node.field.Mandatory ? 'Yes' : 'No');
            tip += '<br /><strong>Visible: </strong>' + (node.field.Visible ? 'Yes' : 'No');

            if (node.field.FieldType === 'Relationship') {
                var dataType;
                if (node.field.RelationshipType === 'Embedded' && node.field.Cardinality === 'One') {
                    dataType = 'Item (one)';
                } else if (node.field.RelationshipType === 'Embedded' && node.field.Cardinality === 'Many') {
                    dataType = 'Items (many)';
                } else if (node.field.RelationshipType === 'Linked' && node.field.Cardinality === 'One') {
                    dataType = 'Linked item';
                } else if (node.field.RelationshipType === 'Linked' && node.field.Cardinality === 'Many') {
                    dataType = 'Linked items';
                } else {
                    dataType = node.field.FieldType;
                }

                tip += '<br /><strong>Data Type</strong>: ' + dataType;

                if (node.relationshipFieldName) {
                    tip += '<br /><strong>Entity Template: </strong>' + node.relationshipFieldName;
                }
            } else {
                tip += '<br /><strong>Data Type: </strong>' + node.field.FieldType;
            }

            if (node.field.FieldType === 'List') {
                tip += '<br /><strong>Selection Mode: </strong>' + node.field.EnumerationType;
            } else if (node.field.FieldType === 'Currency') {
                tip += '<br /><strong>Symbol: </strong>' + node.field.Symbol;
                tip += '<br /><strong>Decimal Places: </strong>' + node.field.Decimal;
            }

            var fieldType = _.find(vm.fieldTypes, { Name: node.field.FieldType });
            if (!_.isNil(fieldType)) {
                _.forEach(fieldType.Attributes, function (attributeName) {
                    if (node.field[attributeName] && node.field[attributeName] !== '') {
                        tip += '<br /><strong>' + attributeName + ': </strong>' + node.field[attributeName];
                    }
                });
            }

            return tip;
        }

        /**
         * Determine whether the provided tree node can be selected.
         *
         * @returns {bool}
         */
        function isTreeNodeSelectable(node) {
            if (!_.isNil(node.isSelectable)) {
                // Selectable explicitly defined
                return node.isSelectable;
            } else if (!_.isNil(node.field) && !_.isNil(node.field.FieldType) && node.field.FieldType === 'Relationship') {
                // Can't select embedded or linked entity fields
                return false;
            } else {
                return true;
            }
        }

        /**
         * Expand all tree nodes.
         */
        function expandAll() {
            _.remove(vm.expandedTreeNodes);

            var fakeRootNode = {
                children: vm.treeData
            };

            expandAllChildren(fakeRootNode);

            function expandAllChildren(node) {
                if (node.children) {
                    vm.expandedTreeNodes.push(node);
                    _.forEach(node.children, expandAllChildren);
                }
            }
        }

        /**
         * Collapse all tree nodes, except:
         * 1. If there is one root node, expand it.
         * 2. If there is more than one root node, collapse all of them.
         */
        function collapseAll() {
            _.remove(vm.expandedTreeNodes);

            if (vm.treeData.length === 1) {
                if (vm.treeData[0].children) {
                    vm.expandedTreeNodes.push(vm.treeData[0]);
                }
            }
        }

        /**
         * By default the tree view is fully collapsed. This expands all nodes necessary to ensure all selected
         * nodes are visible. If there are no selected nodes, it expands all root nodes with children.
         */
        function expandNodesWithSelectedChildren() {
            var selectedNodes = vm.multiSelect ? vm.selectedTreeNodes : [vm.selectedTreeNode];

            // All nodes that are either:
            // a) selected, or;
            // b) an ancestor of a selected node
            var selectedOrAncestorNodes = [];

            var rootNode = {
                children: vm.treeData
            };

            isNodeOrDescendantSelected(rootNode);

            function isNodeOrDescendantSelected(nodeToCheck) {
                var selected = false;

                if (_.indexOf(selectedNodes, nodeToCheck) !== -1) {
                    selected = true;
                }

                if (nodeToCheck.children) {
                    for (var i = 0; i < nodeToCheck.children.length; i++) {
                        if (isNodeOrDescendantSelected(nodeToCheck.children[i])) {
                            selected = true;
                        }
                    }
                }

                if (selected) {
                    selectedOrAncestorNodes.push(nodeToCheck);
                }

                return selected;
            }

            // If there are no selected nodes, expand all root nodes that have children
            if (selectedOrAncestorNodes.length === 0) {
                angular.forEach(vm.treeData, function (rootTreeNode) {
                    if (rootTreeNode.children) {
                        selectedOrAncestorNodes.push(rootTreeNode);
                    }
                });
            }

            _.forEach(selectedOrAncestorNodes, function (n) {
                if (n.children) {
                    vm.expandedTreeNodes.push(n);
                }
            });
        }

        /**
         * Find nodes based on field expressions.
         *
         * @param {array} fieldExpressions - A list of field expressions for the nodes to be found.
         *   E.g. find the node for field expression 'Cats.Species'
         * @param {array} treeData - A list of tree nodes. Each node may or may not have children.
         */
        function findNodesByFieldExpression(fieldExpressions, treeData) {
            return findNodes(fieldExpressions, { children: treeData });

            function findNodes(fieldExpressions, node) {
                var nodes = [];

                if (fieldExpressions.indexOf(node.fieldExpression) !== -1) {
                    nodes.push(node);
                }

                if (node.children) {
                    angular.forEach(node.children, function (childNode) {
                        var foundChildNodes = findNodes(fieldExpressions, childNode);

                        angular.forEach(foundChildNodes, function (foundChild) {
                            nodes.push(foundChild);
                        });
                    });
                }

                return nodes;
            }
        }
    }
})();
